import React from 'react'

import Loadable from 'react-loadable'

import { TypeForm, TypeFormProps } from 'src/components/global/typeForm'
import { Hero, HeroProps } from 'src/components/global/Hero'
import { ImageAndTextProps } from 'src/components/global/imageAndText'
import {
  StandardText,
  StandardTextProps,
} from 'src/components/global/standardText'
import {
  EditorialText,
  EditorialTextProps,
} from 'src/components/global/editorialText'
import { CarouselProps } from 'src/components/global/carousel'
import { TestimonialCarouselProps } from 'src/components/global/testimonialCarousel'
import { Shape, ShapeProps } from 'src/components/global/shape'
import { CTA, CTAProps } from 'src/components/global/cta'
import { Jobs, JobsProps } from 'src/components/global/jobs'
import { Spacer, SpacerProps } from 'src/components/global/spacer'
import { FaqsProps } from 'src/components/global/faqs'
import { PricingBlockProps } from 'src/components/global/pricingBlock'
import { TextGridProps } from 'src/components/global/textGrid'
import { TextCarouselProps } from 'src/components/global/textCarousel'
import { VideoProps } from 'src/components/global/video'
import { JoinWidgetProps } from 'src/components/global/joinWidget'
import { Note, NoteProps } from 'src/components/global/note'
import { AppointmentsModuleProps } from 'src/components/global/appointmentsModule'
import { EventsModuleProps } from 'src/components/global/eventsModule'
import { FurtherReadingProps } from 'src/components/editorial/furtherReading'
import { MembershipCardProps } from 'src/components/global/membershipCard'
import { ImageModuleProps } from 'src/components/global/imageModule'
import {
  TableModule,
  TableModuleProps,
} from 'src/components/global/tableModule'

import { InsuranceModuleProps } from 'src/components/global/insuranceModule'
import { FeaturedArticlesProps } from 'src/components/editorial/featuredArticles'
import { EditorialImageAndTextProps } from 'src/components/editorial/editorialImageAndText'
import { EditorialGridProps } from 'src/components/global/editorialGrid'
import { ExperimentProps } from 'src/components/global/experiment'
import { FeaturedTopicProps } from 'src/components/editorial/featuredTopic'
import { ImageCarouselProps } from 'src/components/global/imageCarousel'
import {
  LocationsList,
  LocationsListProps,
} from 'src/components/locations/locationsList'
import { TeamGridProps } from 'src/components/team/teamGrid'
import EmailCapture from './global/emailCapture'
import { EmailCaptureProps } from './global/interfaces/EmailCapture'
import LocationCarousel, {
  LocationCarouselProps,
} from './locations/locationCarousel'
import Testimonials, { TestimonialProps } from './global/Testimonials'
import { LeadForm, LeadFormProps } from './global/leadForm'
import FullBleedHero, { FullBleedHeroProps } from './global/FullBleedHero'
import { GlobalPricingBlockProps } from './global/interfaces/GlobalPricingBlock'
import { ConditionalProps } from 'src/components/global/Conditional'
import RollingMarquee, { RollingMarqueeProps } from './global/RollingMarquee'
import { SocialProof, SocialProofProps } from './global/SocialProof'
import { SideBySideProps } from 'src/components/global/sideBySide'
import { CareJourney, CareJourneyProps } from './global/CareJourney'
import { CareTeam, CareTeamProps } from './global/CareTeam'

const toClassName = (type: string) => {
  const regex = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;

  const matches = type.match(regex)

  if (matches && matches.length > 0) {
    return matches.map((x) => x.toLowerCase()).join("-")
  }

  return type.toLowerCase()
}

export const Loading = (props: { type: string }) => {
  return (<div className={`loading-${toClassName(props.type)}`} style={{
    minWidth: 'calc(80vw - 1rem)',
    minHeight: 'calc(80vh - 1rem)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }}>
    <span>Loading...</span>
  </div>)
}

export const Modules = ({
  reactModule,
  type,
}: {
  type: string
  reactModule: any
}) => {
  switch (type) {
    case 'textGrid':
      const TextGrid = Loadable({
        loader: () => import('src/components/global/textGrid'),
        loading: () => <Loading type={type}/>,
      })
      return <TextGrid data={reactModule as TextGridProps['data']} />
    case 'typeForm':
      const Typeform = Loadable({
        loader: () => import('src/components/global/typeForm'),
        loading: () => <Loading type={type}/>,
      })
      return <TypeForm data={reactModule as TypeFormProps['data']} />
    case 'editorialGrid':
      const EditorialGrid = Loadable({
        loader: () => import('src/components/global/editorialGrid'),
        loading: () => <Loading type={type}/>,
      })
      return <EditorialGrid data={reactModule as EditorialGridProps['data']} />
    case 'appointmentsModule':
      const AppointmentsModule = Loadable({
        loader: () => import('src/components/global/appointmentsModule'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <AppointmentsModule
          data={reactModule as AppointmentsModuleProps['data']}
        />
      )
    case 'sideBySide':
      const SideBySideModule = Loadable({
        loader: () => import('src/components/global/sideBySide'),
        loading: () => <Loading type={type}/>,
      })
      return <SideBySideModule data={reactModule as SideBySideProps['data']} />
    case 'textCarousel':
      const TextCarousel = Loadable({
        loader: () => import('src/components/global/textCarousel'),
        loading: () => <Loading type={type}/>,
      })
      return <TextCarousel data={reactModule as TextCarouselProps['data']} />
    case 'eventsModule':
      const EventsModule = Loadable({
        loader: () => import('src/components/global/eventsModule'),
        loading: () => <Loading type={type}/>,
      })
      return <EventsModule data={reactModule as EventsModuleProps['data']} />
    case 'experiment':
      const Experiment = Loadable({
        loader: () => import('src/components/global/experiment'),
        loading: () => <Loading type={type}/>,
      })
      return <Experiment data={reactModule as ExperimentProps['data']} />

    case 'conditional':
      const Conditional = Loadable({
        loader: () => import('src/components/global/Conditional'),
        loading: () => <Loading type={type}/>,
      })
      return <Conditional data={reactModule as ConditionalProps['data']} />
    case 'spacer':
      return <Spacer data={reactModule as SpacerProps['data']} />
    case 'furtherReading':
      const FurtherReading = Loadable({
        loader: () => import('src/components/editorial/furtherReading'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <FurtherReading data={reactModule as FurtherReadingProps['data']} />
      )
    case 'joinWidget':
      const JoinWidget = Loadable({
        loader: () => import('src/components/global/joinWidget'),
        loading: () => <Loading type={type}/>,
      })
      return <JoinWidget data={reactModule as JoinWidgetProps['data']} />
    case 'membershipCard':
      const MembershipCard = Loadable({
        loader: () => import('src/components/global/membershipCard'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <MembershipCard data={reactModule as MembershipCardProps['data']} />
      )
    case 'note':
      return <Note data={reactModule as NoteProps['data']} />
    case 'editorialImageAndText':
      const EditorialImageAndText = Loadable({
        loader: () => import('src/components/editorial/editorialImageAndText'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <EditorialImageAndText
          data={reactModule as EditorialImageAndTextProps['data']}
        />
      )
    case 'tableModule':
      return <TableModule data={reactModule as TableModuleProps['data']} />
    case 'editorialText':
      return <EditorialText data={reactModule as EditorialTextProps['data']} />
    case 'testimonialCarousel':
      const TestimonialCarousel = Loadable({
        loader: () => import('src/components/global/testimonialCarousel'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <TestimonialCarousel
          data={reactModule as TestimonialCarouselProps['data']}
        />
      )
    case 'insuranceModule':
      const InsuranceModule = Loadable({
        loader: () => import('src/components/global/insuranceModule'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <InsuranceModule data={reactModule as InsuranceModuleProps['data']} />
      )
    case 'imageAndText':
      const ImageAndText = Loadable({
        loader: () => import('src/components/global/imageAndText'),
        loading: () => <Loading type={type}/>,
      })
      return <ImageAndText data={reactModule as ImageAndTextProps['data']} />
    case 'standardText':
      return <StandardText data={reactModule as StandardTextProps['data']} />
    case 'carousel':
      const Carousel = Loadable({
        loader: () => import('src/components/global/carousel'),
        loading: () => <Loading type={type}/>,
      })
      return <Carousel data={reactModule as CarouselProps['data']} />
    case 'cta':
      return <CTA data={reactModule as CTAProps['data']} />
    case 'locationsList':
      return <LocationsList data={reactModule as LocationsListProps['data']} />
    case 'featuredTopic':
      const FeaturedTopic = Loadable({
        loader: () => import('src/components/editorial/featuredTopic'),
        loading: () => <Loading type={type}/>,
      })
      return <FeaturedTopic data={reactModule as FeaturedTopicProps['data']} />
    case 'featuredArticles':
      const FeaturedArticles = Loadable({
        loader: () => import('src/components/editorial/featuredArticles'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <FeaturedArticles data={reactModule as FeaturedArticlesProps['data']} />
      )
    case 'faqModule':
      const Faqs = Loadable({
        loader: () => import('src/components/global/faqs'),
        loading: () => <Loading type={type}/>,
      })
      return <Faqs data={reactModule as FaqsProps['data']} />
    case 'video':
      const Video = Loadable({
        loader: () => import('src/components/global/video'),
        loading: () => <Loading type={type}/>,
      })
      return <Video data={reactModule as VideoProps['data']} />
    case 'imageCarousel':
      const ImageCarousel = Loadable({
        loader: () => import('src/components/global/imageCarousel'),
        loading: () => <Loading type={type}/>,
      })
      return <ImageCarousel data={reactModule as ImageCarouselProps['data']} />
    case 'jobs':
      return <Jobs data={reactModule as JobsProps['data']} />
    case 'teamGrid':
      const TeamGrid = Loadable({
        loader: () => import('src/components/team/teamGrid'),
        loading: () => <Loading type={type}/>,
      })
      return <TeamGrid data={reactModule as TeamGridProps['data']} />
    case 'imageModule':
      const ImageModule = Loadable({
        loader: () => import('src/components/global/imageModule'),
        loading: () => <Loading type={type}/>,
      })
      return <ImageModule data={reactModule as ImageModuleProps['data']} />

    case 'pricingBlock':
      const PricingBlock = Loadable({
        loader: () => import('src/components/global/pricingBlock'),
        loading: () => <Loading type={type}/>,
      })
      return <PricingBlock data={reactModule as PricingBlockProps['data']} />
    case 'globalPricingBlock':
      const GlobalPricingBlock = Loadable({
        loader: () => import('src/components/global/GlobalPricingBlock'),
        loading: () => <Loading type={type}/>,
      })
      return (
        <GlobalPricingBlock
          data={reactModule as { data: GlobalPricingBlockProps }}
        />
      )
    case 'shape':
      return <Shape data={reactModule as ShapeProps['data']} />
    case 'hero':
      return <Hero data={reactModule as HeroProps['data']} />
    case 'fullBleedHero':
      return <FullBleedHero data={reactModule as FullBleedHeroProps['data']} />
    case 'rollingMarquee':
      return (
        <RollingMarquee data={reactModule as RollingMarqueeProps['data']} />
      )
    case 'leadForm':
      return <LeadForm data={reactModule as LeadFormProps['data']} />
    case 'testimonials':
      return <Testimonials data={reactModule as TestimonialProps['data']} />
    case 'emailCapture':
      return <EmailCapture data={reactModule as { data: EmailCaptureProps }} />
    case 'cardCarousel':
      return (
        <LocationCarousel
          data={reactModule as { data: LocationCarouselProps }}
        />
      )
    case 'socialProof':
      return <SocialProof data={reactModule as SocialProofProps} />
    case 'careJourney':
      return <CareJourney data={reactModule as CareJourneyProps} />
    case 'careTeam':
      return <CareTeam data={reactModule as CareTeamProps} />
    default:
      return <h2 className="hide">{type} (coming soon :))</h2>
  }
}
